import { useCallback, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddFactDialog } from './dialogs/add-fact-dialog';
import { AddToChronologyDialog } from './dialogs/add-to-chronology-dialog';
import { ArchiveSelectedButton } from './dialogs/archive-selected';
import { FilterFactsDialog } from './dialogs/filter-facts-dialog';
import { UpdateRelevanceButton } from './dialogs/update-relevance';
import { useMatterProcessingStore } from '../../../../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateChronology } from '@/api/mutations/useCreateChronology';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { useGetChronologies } from '@/api/queries/useGetChronologies';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Person, Run } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { ClusterFactsModal } from '@/new/components/labeller/cluster-facts';
import { CreateInconsistencyModal } from '@/new/components/labeller/create-inconsistency';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetChip } from '@/new/components/subset-chip';
import { addFactsBatchToVisualisation } from '@/screens/Chronos/CaseEditor/DataView/ChronologyViewer/ChronologyEditor/VisualTimeline/SyncingHelpers/syncVisualisationWithTimelineChanges';
import { FactFilters as FactFiltersType } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  caseRuns: Run[];
  allPersons: Person[];
  onClusterComplete: () => void;
  processingNL: boolean;
  filterCount: number;
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
}

export const FactsTableHeader = ({
  globalFilter,
  selectedFactIds,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  onSearchCall,
  goToPage,
  activeFilters,
  setFilters,
  caseRuns,
  allPersons,
  onClusterComplete,
  processingNL,
  filterCount,
  setSelectedFactIds,
}: FactsEditorToolbarProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }

  const { data: themesWithCounts = [] } = useGetCaseThemes(subsetId);
  const { data: docTypes = [] } = useGetCaseDocTypes({ caseId: subsetId });

  const caseThemes = themesWithCounts.map((theme) => theme.name);
  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [selectChronologyModalIsOpen, setSelectChronologyModalIsOpen] = useState(false);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);

  const navigate = useNavigate();
  const authInfo = useAuthInfo();

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  // Data fetching
  const { data: docIdsResponse } = useGetDocIds(subsetId);
  const { data: chronologiesResponse, isLoading: isLoadingChronologies } = useGetChronologies({
    caseId: subsetId,
    paginated: false,
  });

  const { createChronology } = useCreateChronology({
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/${data.chronologyId}`, { replace: true });
    },
    onError: (error) => {
      toast.error('Failed to create chronology');
    },
  });

  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEventsV2.AddFact, { matterId });
    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, matterId]);

  const handleCreateChronology = async () => {
    await createChronology.mutateAsync({
      caseId: subsetId,
      chronologyId: null,
      type: null,
      factIds: Array.from(selectedFactIds),
    });
    trackCustomEvent(TrackingEventsV2.CreateFactFromFactsPage, { matterId });
  };

  const handleSelectChronology = async (chronologyId: string) => {
    setSelectChronologyModalIsOpen(false);
    try {
      // Add facts to the chronology
      const result = await createChronology.mutateAsync({
        caseId: subsetId,
        chronologyId,
        type: null,
        factIds: Array.from(selectedFactIds),
      });

      // If facts were added to an existing chronology, sync the visual timeline
      if (result.addedFacts && result.addedFacts.length > 0) {
        await addFactsBatchToVisualisation({
          caseId: subsetId!,
          chronologyId,
          newFacts: result.addedFacts,
          getAccessToken: authInfo.tokens.getAccessToken,
        });
      }

      trackCustomEvent(TrackingEventsV2.AddToChronologyFromFactsPage, { matterId, chronologyId });
    } catch (error) {
      console.error('Error adding facts to chronology:', error);
      toast.error('Failed to add facts to chronology');
    }
  };

  const resultsIncludedLength = selectedFactIds.size;

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-4">
          <PageTitle text="Facts" />
          <SubsetChip subsetId={subsetId} className="mt-0.5" />
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={resultsIncludedLength === 0 || createChronology.isLoading}>
                {createChronology.isLoading ? (
                  <Icon className="lucide--loader-2 animate-spin duration-700" />
                ) : (
                  <Icon className="lucide--git-fork" />
                )}
                Chronology
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom">
              <DropdownMenuItem onClick={handleCreateChronology}>Create New</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectChronologyModalIsOpen(true)}>Add to Existing</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {isLabellingOrg && (
            <>
              <Button disabled={resultsIncludedLength < 2} onClick={() => setClusterModalOpen(true)}>
                <Icon className="lucide--group" />
                Create Cluster
              </Button>

              <Button disabled={resultsIncludedLength < 2} onClick={() => setCreateInconsistencyModalOpen(true)}>
                <Icon className="lucide--triangle-alert" />
                Create Inconsistency
              </Button>
            </>
          )}

          <ButtonGroup>
            <UpdateRelevanceButton selectedFactIds={selectedFactIds} />
            <ArchiveSelectedButton selectedFactIds={selectedFactIds} setSelectedFactIds={setSelectedFactIds} />
          </ButtonGroup>

          <Button
            variant="primary"
            onClick={() => setFactAdderModalIsOpen(true)}
            disabled={!getProcessingState(matterId)?.hasCompletedRuns}
          >
            <Icon className="lucide--plus" />
            Add Fact
          </Button>

          <AddFactDialog
            isOpen={factAdderModalIsOpen}
            onOpenChange={setFactAdderModalIsOpen}
            docs={docIdsResponse?.documents ?? []}
            handleNewFactCreated={handleNewFactCreated}
          />
        </div>
      </PageHeader>

      <div className="flex w-full gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="facts"
        />

        <ButtonGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.disputedFacts ? 'destructive' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    disputedFacts: !activeFilters.disputedFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'border-r-0!' : ''}
              >
                <Icon className="lucide--triangle-alert" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle disputed Facts</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.keyFacts ? 'warn' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    keyFacts: !activeFilters.keyFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'z-10 not-hover:border-l!' : ''}
              >
                <Icon className="lucide--key-round" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle key Facts</TooltipContent>
          </Tooltip>
        </ButtonGroup>

        <FilterFactsDialog
          isOpen={filtersModalOpen}
          onOpenChange={setFiltersModalOpen}
          filterCount={filterCount}
          activeFilters={activeFilters}
          allDocumentTypes={docTypes}
          setFilters={setFilters}
          caseRuns={caseRuns}
          caseThemes={caseThemes}
          allPersons={allPersons}
          goToPage={goToPage}
        />
      </div>

      <AddToChronologyDialog
        isOpen={selectChronologyModalIsOpen}
        onOpenChange={setSelectChronologyModalIsOpen}
        isLoadingChronologies={isLoadingChronologies}
        chronologies={chronologiesResponse?.chronologies ?? []}
        handleSelectChronology={handleSelectChronology}
        isAddingFacts={createChronology.isLoading}
      />

      {clusterModalOpen && (
        <ClusterFactsModal
          isOpen={clusterModalOpen}
          onClose={() => setClusterModalOpen(false)}
          caseId={matterId}
          onSuccess={() => {
            setClusterModalOpen(false);
            onClusterComplete();
          }}
        />
      )}
      {createInconsistencyModalOpen && (
        <CreateInconsistencyModal
          isOpen={createInconsistencyModalOpen}
          onClose={() => setCreateInconsistencyModalOpen(false)}
          caseId={matterId}
          onSuccess={() => {
            setCreateInconsistencyModalOpen(false);
            refetchCaseFacts();
          }}
        />
      )}
    </>
  );
};
